import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import ImageGallery from './components/ImageGallery';
import TrailerSpecifications from './components/TrailerSpecifications';
import AvailabilityCalendar from './components/AvailabilityCalendar';
import OwnerProfile from './components/OwnerProfile';
import PricingBreakdown from './components/PricingBreakdown';
import ReviewsSection from './components/ReviewsSection';
import { fetchTrailerListingById, fetchTrailerReviews } from '../../services/trailerListingService';
import { supabase } from '../../lib/supabase';
import ErrorFallback from '../../components/ui/ErrorFallback';

const TrailerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedDates, setSelectedDates] = useState({ start: null, end: null });
  const [selectedPickupTime, setSelectedPickupTime] = useState('');
  const [selectedDropoffTime, setSelectedDropoffTime] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [trailerData, setTrailerData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [error, setError] = useState(null);

  // Fetch trailer data by ID
  useEffect(() => {
    const fetchTrailer = async () => {
      if (!id) {
        navigate('/search-results');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await fetchTrailerListingById(id);
        if (!data) {
          navigate('/search-results');
          return;
        }
        setTrailerData(data);

        // Fetch bookings for availability
        const { data: bookingsData } = await supabase
          ?.from('bookings')
          ?.select('start_date, end_date, status')
          ?.eq('trailer_listing_id', id)
          ?.in('status', ['confirmed', 'pending']);

        setBookings(bookingsData || []);

        // Fetch blocked dates set by owner
        const { data: blockedData } = await supabase
          ?.from('blocked_dates')
          ?.select('start_date, end_date')
          ?.eq('trailer_listing_id', id)
          ?.eq('is_active', true);

        setBlockedDates(blockedData || []);

        // Fetch real reviews for this trailer
        const trailerReviews = await fetchTrailerReviews(id);
        setReviews(trailerReviews);
      } catch (error) {
        console.error('Error fetching trailer:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrailer();
  }, [id, navigate]);

  // Calculate available dates (exclude booked and blocked dates)
  const getAvailableDates = () => {
    const availableDates = [];
    const today = new Date();
    const threeMonthsLater = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());

    for (let d = new Date(today); d <= threeMonthsLater; d?.setDate(d?.getDate() + 1)) {
      const dateString = d?.toISOString()?.split('T')?.[0];
      
      // Check if date is not booked
      const isBooked = bookings?.some(booking => {
        const startDate = new Date(booking?.start_date);
        const endDate = new Date(booking?.end_date);
        return d >= startDate && d <= endDate;
      });

      // Check if date is not blocked by owner
      const isBlocked = blockedDates?.some(block => {
        const blockStart = new Date(block?.start_date);
        const blockEnd = block?.end_date ? new Date(block?.end_date) : blockStart;
        return d >= blockStart && d <= blockEnd;
      });

      if (!isBooked && !isBlocked) {
        availableDates?.push(dateString);
      }
    }

    return availableDates;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <div className="container mx-auto px-4 py-8">
          <div className="space-y-6 animate-pulse">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-96 bg-muted rounded" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-64 bg-muted rounded" />
                <div className="h-48 bg-muted rounded" />
              </div>
              <div className="space-y-6">
                <div className="h-96 bg-muted rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <div className="container mx-auto px-4 py-8">
          <ErrorFallback 
            error={error} 
            onRetry={() => window.location?.reload()} 
            title="Failed to load trailer details"
          />
        </div>
      </div>
    );
  }

  if (!trailerData) {
    return null;
  }

  // Transform reviews for display
  const transformedReviews = reviews?.map(review => ({
    id: review?.id,
    userName: review?.reviewer?.fullName || 'Anonymous',
    userAvatar: review?.reviewer?.profilePhotoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1fc38413b-1763294138349.png',
    userAvatarAlt: `${review?.reviewer?.fullName || 'Reviewer'} profile photo`,
    rating: review?.overallRating || 0,
    date: review?.createdAt,
    comment: review?.comment || '',
    response: null
  }));

  // Calculate average rating from real reviews
  const averageRating = transformedReviews?.length > 0
    ? parseFloat((transformedReviews?.reduce((sum, r) => sum + r?.rating, 0) / transformedReviews?.length)?.toFixed(1))
    : parseFloat(trailerData?.averageRating || 0);

  // Transform data for components
  const transformedData = {
    id: trailerData?.id,
    title: trailerData?.title,
    type: trailerData?.trailerType,
    description: trailerData?.description,
    location: `${trailerData?.locationCity}, ${trailerData?.locationState}`,
    images: trailerData?.photos?.map((url, index) => ({
      url,
      alt: `${trailerData?.title} - view ${index + 1}`
    })) || [{
      url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png',
      alt: `${trailerData?.title} - default view`
    }],
    specifications: {
      length: trailerData?.lengthFeet ? `${trailerData?.lengthFeet} feet` : null,
      width: trailerData?.widthFeet ? `${trailerData?.widthFeet} feet` : null,
      height: trailerData?.heightFeet ? `${trailerData?.heightFeet} feet` : null,
      weightCapacity: trailerData?.weightCapacityLbs ? `${trailerData?.weightCapacityLbs?.toLocaleString()} lbs` : null,
      hitchSize: trailerData?.hitchSize || null,
      hitchType: trailerData?.trailerType ? trailerData?.trailerType?.replace(/_/g, ' ') : null,
      year: trailerData?.year || null,
      make: trailerData?.make || null,
      model: trailerData?.model || null,
      features: Array.isArray(trailerData?.features) ? trailerData?.features : []
    },
    pricing: {
      dailyRate: parseFloat(trailerData?.dailyRate || 0),
      weeklyRate: parseFloat(trailerData?.weeklyRate || 0),
      monthlyRate: parseFloat(trailerData?.monthlyRate || 0),
      securityDeposit: parseFloat(trailerData?.securityDeposit || 0),
      cleaningFee: parseFloat(trailerData?.cleaningFee || 0),
      deliveryFee: parseFloat(trailerData?.deliveryFee || 0),
      weeklyDiscount: trailerData?.weeklyRate
        ? Math.round((1 - (parseFloat(trailerData?.weeklyRate) / (parseFloat(trailerData?.dailyRate) * 7))) * 100)
        : 0,
      additionalFees: trailerData?.deliveryFee > 0
        ? [{ name: 'Delivery Fee', amount: parseFloat(trailerData?.deliveryFee) }]
        : []
    },
    owner: {
      id: trailerData?.owner?.id,
      name: trailerData?.owner?.fullName || 'Owner',
      avatar: trailerData?.owner?.profilePhotoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1fc38413b-1763294138349.png',
      avatarAlt: `${trailerData?.owner?.fullName || 'Owner'} profile photo`,
      verified: trailerData?.owner?.verifiedStatus || false,
      rating: trailerData?.ownerAverageRating || parseFloat(trailerData?.averageRating || 0),
      reviewCount: trailerData?.ownerReviewCount || 0,
      location: [trailerData?.owner?.city, trailerData?.owner?.state]?.filter(Boolean)?.join(', ') || trailerData?.locationCity,
      memberSince: trailerData?.owner?.createdAt ? new Date(trailerData?.owner?.createdAt)?.getFullYear() : new Date(trailerData?.createdAt)?.getFullYear(),
      totalTrailers: trailerData?.ownerListingCount || 1,
      responseTime: '2 hours',
      completedRentals: trailerData?.bookingCount || 0,
      bio: trailerData?.owner?.bio || ''
    },
    reviews: transformedReviews,
    availability: getAvailableDates()
  };

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": transformedData?.title,
    "description": transformedData?.description,
    "image": transformedData?.images?.map((img) => img?.url) || [],
    "offers": {
      "@type": "Offer",
      "price": transformedData?.pricing?.dailyRate || 0,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1))?.toISOString()?.split('T')?.[0],
      "seller": {
        "@type": "Person",
        "name": transformedData?.owner?.name || "Owner"
      }
    },
    "aggregateRating": transformedData?.reviews?.length > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": averageRating || 0,
      "reviewCount": transformedData?.reviews?.length || 0,
      "bestRating": "5",
      "worstRating": "1"
    } : undefined,
    "brand": {
      "@type": "Brand",
      "name": "Draggin Trailers"
    },
    "category": transformedData?.type
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://draggintrailers.com"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Search Results",
      "item": "https://draggintrailers.com/search"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": transformedData?.title,
      "item": `https://draggintrailers.com/trailer/${transformedData?.id}`
    }]
  };

  const pageTitle = `${transformedData?.title} - Rent in ${transformedData?.location} | Draggin Trailers`;
  const pageDescription = `Rent ${transformedData?.title} in ${transformedData?.location} for $${transformedData?.pricing?.dailyRate}/day. ${transformedData?.specifications?.weightCapacity ? transformedData?.specifications?.weightCapacity + ' capacity, ' : ''}${transformedData?.specifications?.length ? transformedData?.specifications?.length + ' long. ' : ''}Book now with verified owner.`;
  const canonicalUrl = `https://draggintrailers.com/trailer/${transformedData?.id}`;
  const imageUrl = transformedData?.images?.[0]?.url || '';

  const handleDateSelect = (startDate, endDate, pickupTime, dropoffTime) => {
    setSelectedDates({ start: startDate, end: endDate });
    if (pickupTime !== undefined) setSelectedPickupTime(pickupTime);
    if (dropoffTime !== undefined) setSelectedDropoffTime(dropoffTime);
  };

  const handleBookNow = () => {
    if (!selectedDates?.start || !selectedDates?.end) {
      alert('Please select rental dates before booking');
      return;
    }

    navigate('/checkout', {
      state: {
        bookingData: {
          trailerId: transformedData?.id,
          startDate: selectedDates?.start?.toISOString()?.split('T')?.[0],
          endDate: selectedDates?.end?.toISOString()?.split('T')?.[0],
          pickupTime: selectedPickupTime || null,
          dropoffTime: selectedDropoffTime || null
        }
      }
    });
  };

  const handleContactOwner = () => {
    setShowContactModal(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      </Helmet>

      <MainNavigation />

      <div className="container mx-auto px-4 py-6">
        <BreadcrumbNavigation
          items={[
            { label: 'Home', path: '/' },
            { label: 'Search Results', path: '/search-results' },
            { label: transformedData?.title, path: `/trailer-detail/${transformedData?.id}` }
          ]}
        />
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <ImageGallery images={transformedData?.images} />

            <div className="bg-card rounded-lg shadow-elevation-2 p-4 md:p-6 lg:p-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                <div className="flex-1">
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                    {transformedData?.title}
                  </h1>
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={18} strokeWidth={2} />
                      <span className="font-body text-base">{transformedData?.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Tag" size={18} strokeWidth={2} />
                      <span className="font-body text-base capitalize">{transformedData?.type?.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                  {averageRating > 0 && (
                    <div className="flex items-center space-x-2 mt-2">
                      <Icon name="Star" size={16} color="var(--color-accent)" strokeWidth={2} fill="var(--color-accent)" />
                      <span className="font-body text-sm font-semibold text-foreground">{averageRating}</span>
                      <span className="font-body text-sm text-muted-foreground">({transformedData?.reviews?.length} reviews)</span>
                    </div>
                  )}
                </div>
                <div className="mt-4 md:mt-0 text-right">
                  <p className="font-heading text-4xl font-bold text-primary">
                    ${transformedData?.pricing?.dailyRate}
                  </p>
                  <p className="font-caption text-sm text-muted-foreground">per day</p>
                  {transformedData?.pricing?.weeklyDiscount > 0 && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-success/10 text-success text-xs font-caption font-medium rounded">
                      {transformedData?.pricing?.weeklyDiscount}% weekly discount
                    </span>
                  )}
                </div>
              </div>

              <div className="prose prose-sm md:prose-base max-w-none">
                <p className="font-body text-foreground leading-relaxed whitespace-pre-line">
                  {transformedData?.description}
                </p>
              </div>
            </div>

            <TrailerSpecifications specifications={transformedData?.specifications} />
            <ReviewsSection 
              reviews={transformedData?.reviews} 
              averageRating={averageRating} 
              totalReviews={transformedData?.reviews?.length || 0}
            />
          </div>

          <div className="space-y-6">
            <PricingBreakdown
              pricing={transformedData?.pricing}
              selectedDates={selectedDates}
              onBookNow={handleBookNow}
            />
            <AvailabilityCalendar
              availability={transformedData?.availability}
              ownerPickupTime={trailerData?.pickupTime || null}
              ownerDropoffTime={trailerData?.dropoffTime || null}
              onDateSelect={handleDateSelect}
            />
            <OwnerProfile owner={transformedData?.owner} onContact={handleContactOwner} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrailerDetail;